"use client";

import { useEffect } from "react";
import { useParams } from "next/navigation";

import { useNamespaceStore } from "@/state/namespace-store";
import type { NamespaceId, NamespaceSummary } from "@/types/namespaces";

/**
 * Legacy query param name. URLs no longer carry `?namespaceId=`; the namespace
 * is part of the path (`/{slug}/...`). The constant is kept so existing
 * top-bar menu code can still strip it from older bookmarks.
 */
export const NAMESPACE_QUERY_PARAM = "namespaceId";

/**
 * Hydrates the namespace store from the active route's `[slug]` param. Used by
 * the workspace-scoped views (tasks, files, documents, etc.) so the rest of
 * the UI — the assistant overlay, queries, the top-bar selector — picks up
 * the namespace implied by the URL.
 */
export function useNamespaceQueryParamHydrator(namespaces?: NamespaceSummary[] | null) {
  const params = useParams();
  const setSelectedNamespaceId = useNamespaceStore((state) => state.setSelectedNamespaceId);

  useEffect(() => {
    if (!namespaces?.length) return;

    const rawSlug = params?.slug;
    const slug = (typeof rawSlug === "string"
      ? rawSlug
      : Array.isArray(rawSlug) && rawSlug.length > 0
        ? rawSlug[0]
        : null)?.toLowerCase();
    if (!slug) return;

    const match = namespaces.find((n) => (n.slug ?? "").toLowerCase() === slug);
    if (!match) return;

    // Read non-reactively to avoid a render loop when other consumers update
    // the store. We only push URL → store, never the other way around.
    const currentSelectedId = useNamespaceStore.getState().selectedNamespaceId;
    if (match.namespaceId === currentSelectedId) return;

    setSelectedNamespaceId(match.namespaceId as NamespaceId);
  }, [namespaces, params, setSelectedNamespaceId]);
}
